import { useEffect, useState } from "react";
import { Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/hooks/useLanguage";
import { detectPlatform, installState, isInAppBrowser, isStandalone } from "@/lib/installPrompt";

const AddToHomeScreen = () => {
  const { t, isRTL } = useLanguage();
  const [open, setOpen] = useState(false);
  const [hidden, setHidden] = useState(() => isStandalone() || installState.installed());
  const [canPrompt, setCanPrompt] = useState(installState.canPrompt());
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => installState.subscribe(() => {
    setCanPrompt(installState.canPrompt());
    if (installState.installed()) { setHidden(true); setOpen(false); }
  }), []);

  useEffect(() => {
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const on = () => mq?.matches && setHidden(true);
    mq?.addEventListener?.("change", on);
    return () => mq?.removeEventListener?.("change", on);
  }, []);

  if (hidden) return null;

  const ua = navigator.userAgent;
  const platform = detectPlatform(ua, navigator.maxTouchPoints);
  const inApp = isInAppBrowser(ua);
  const showIos = platform !== "android";
  const showAndroid = platform !== "ios";

  const runPrompt = async () => {
    setNote(null);
    const r = await installState.prompt();
    if (r === "dismissed") setNote(t("install.dismissed"));
    else if (r === "unavailable") setNote(t("install.failed"));
    // "accepted": the button hides when the browser reports appinstalled; no success is claimed here.
  };

  const Steps = ({ title, steps }: { title: string; steps: string[] }) => (
    <section className="space-y-2">
      <h3 className="font-semibold text-foreground text-base">{title}</h3>
      <ol className="list-decimal ps-5 space-y-1 text-sm text-muted-foreground">
        {steps.map((s) => <li key={s}>{s}</li>)}
      </ol>
    </section>
  );

  return (
    <>
      <button
        type="button"
        onClick={() => { setNote(null); setOpen(true); }}
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-gold transition-colors text-sm leading-5 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
      >
        <Smartphone className="h-4 w-4" aria-hidden="true" />
        {t("install.cta")}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir={isRTL ? "rtl" : "ltr"} className="max-w-md max-h-[85vh] overflow-y-auto text-start">
          <DialogHeader className="text-start sm:text-start px-6">
            <DialogTitle className="flex items-center gap-3 text-foreground">
              <img src="/anaya-gold-v1-apple-180.png" alt="" width={40} height={40} className="h-10 w-10 rounded-lg" />
              {t("install.title")}
            </DialogTitle>
            <DialogDescription>{t("install.intro")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            {inApp && (
              <div className="rounded-md border border-gold/40 bg-gold/10 p-3 text-sm" role="note">
                <p className="font-semibold text-foreground">{t("install.inAppTitle")}</p>
                <p className="text-muted-foreground mt-1">{t("install.inAppText")}</p>
              </div>
            )}
            {canPrompt && !inApp && (
              <Button onClick={runPrompt} className="w-full">{t("install.installNow")}</Button>
            )}
            {note && <p className="text-sm text-muted-foreground" role="status">{note}</p>}
            {showIos && <Steps title={t("install.iosTitle")} steps={[t("install.ios1"), t("install.ios2"), t("install.ios3")]} />}
            {showAndroid && <Steps title={t("install.androidTitle")} steps={[t("install.android1"), t("install.android2"), t("install.android3")]} />}
            {!inApp && <p className="text-xs text-muted-foreground"><span className="font-medium">{t("install.inAppTitle")}</span> {t("install.inAppText")}</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AddToHomeScreen;
